
import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiExternalLink, FiBriefcase, FiMapPin, FiUsers, FiLoader} = FiIcons;

const Jobs = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const jobStats = [
    { icon: FiBriefcase, value: '15+', label: 'Open Positions' },
    { icon: FiMapPin, value: '5', label: 'Locations' },
    { icon: FiUsers, value: '50+', label: 'Team Members' }
  ];

  useEffect(() => {
    // Handle iframe load events
    const handleMessage = (event) => {
      // Only accept messages from trusted origins
      if (event.origin === 'https://jobs.trbe.ai') {
        if (event.data === 'iframe-loaded') {
          setIframeLoaded(true);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-purple to-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Find Your Dream Job in Deep Tech
            </h1>
            <p className="text-xl text-brand-lightblue max-w-3xl mx-auto mb-8">
              Discover exciting opportunities in robotics, AI, and computer vision with innovative companies in Germany.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://jobs.trbe.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-brand-orange text-white px-8 py-3 rounded-lg font-medium hover:bg-secondary-500 transition-colors duration-200"
              >
                <span>View All Positions</span>
                <SafeIcon icon={FiExternalLink} className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {jobStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-lightblue rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="h-8 w-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-brand-purple mb-2">{stat.value}</div>
                <div className="text-brand-blue">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Embedded Jobs Page */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-brand-purple to-brand-blue">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Current Openings</h2>
                <a
                  href="https://jobs.trbe.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-brand-lightblue hover:text-white transition-colors"
                >
                  <span className="mr-2">View Full Page</span>
                  <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Loading State */}
            {!iframeLoaded && (
              <div className="flex items-center justify-center py-20 bg-gray-50">
                <div className="text-center">
                  <SafeIcon icon={FiLoader} className="h-8 w-8 text-brand-purple animate-spin mx-auto mb-4" />
                  <p className="text-brand-purple font-medium">Loading job opportunities...</p>
                </div>
              </div>
            )}

            {/* Iframe Container */}
            <div className="relative">
              <iframe
                src="https://jobs.trbe.ai"
                className={`w-full border-0 transition-opacity duration-300 ${
                  iframeLoaded ? 'opacity-100' : 'opacity-0 absolute'
                }`}
                style={{ minHeight: '800px', height: '100vh' }}
                title="Trbe Careers"
                onLoad={() => setIframeLoaded(true)}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                loading="lazy"
              />
            </div>

            {/* Fallback Content */}
            <div className="p-8 text-center border-t border-gray-200">
              <p className="text-gray-600 mb-4">
                Having trouble viewing the job listings?
              </p>
              <a
                href="https://jobs.trbe.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-brand-orange text-white px-6 py-3 rounded-lg font-medium hover:bg-secondary-500 transition-colors duration-200"
              >
                <span>Visit Our Jobs Page Directly</span>
                <SafeIcon icon={FiExternalLink} className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-brand-purple">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Don't See the Perfect Role?
            </h2>
            <p className="text-xl text-brand-lightblue mb-8 max-w-2xl mx-auto">
              We're always looking for exceptional talent. Submit your CV and we'll keep you in mind for future opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:candidate@trbe.ai"
                className="bg-brand-orange text-white px-8 py-3 rounded-lg font-medium hover:bg-secondary-500 transition-colors duration-200"
              >
                Submit Your CV
              </a>
              <a
                href="mailto:hello@trbe.ai"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-brand-purple transition-colors duration-200"
              >
                Get in Touch
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Jobs;
